import { useState } from "react";
import { History, Trash2, Copy, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/lib/toast";
import { haptic } from "@/lib/haptic";
import { useI18n } from "@/lib/i18n";

export default function AddressHistory({ addressHistory, clearAddressHistory }) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useI18n();

  if (addressHistory.length === 0) return null;

  const copyAddress = async (address) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(address);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = address;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      haptic("light");
      toast.success(t.toastCopied, { description: address });
    } catch {
      toast.error(t.toastCopyError);
    }
  };

  const formatHistoryDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t.justNow;
    if (diffMins < 60) return t.minutesAgo(diffMins);
    if (diffHours < 24) return t.hoursAgo(diffHours);
    if (diffDays < 7) return t.daysAgo(diffDays);
    return date.toLocaleDateString(t.dateLocale, { day: "2-digit", month: "2-digit" });
  };

  return (
    <div className="col-span-12">
      <div className="border border-zinc-800 bg-zinc-950/80">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-zinc-900/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-amber-400" />
            <span className="font-mono text-xs sm:text-sm uppercase tracking-widest text-zinc-400">
              {t.addressHistory}
            </span>
            <Badge className="ml-2 bg-amber-500/10 text-amber-400 border-amber-500/30 font-mono">
              {addressHistory.length}
            </Badge>
          </div>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 text-zinc-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-zinc-500" />
          )}
        </button>

        {isOpen && (
          <>
            <div className="divide-y divide-zinc-800/50 max-h-[300px] overflow-auto">
              {addressHistory.map((entry, index) => (
                <div
                  key={`${entry.address}-${index}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-zinc-900/30 transition-colors group"
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-xs sm:text-sm text-cyan-400 truncate block">
                      {entry.address}
                    </span>
                    <span className="font-mono text-[10px] text-zinc-600">
                      {formatHistoryDate(entry.createdAt)}
                    </span>
                  </div>
                  <Button
                    onClick={() => copyAddress(entry.address)}
                    variant="ghost"
                    size="sm"
                    className="text-zinc-600 hover:text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity sm:opacity-100 h-7 w-7 p-0"
                    title={t.copy}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="border-t border-zinc-800 p-3 flex justify-end">
              <Button
                onClick={clearAddressHistory}
                variant="ghost"
                size="sm"
                className="text-red-500/60 hover:text-red-400 hover:bg-red-500/10 font-mono text-xs"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                {t.clearHistory}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
